/* Phase 36 Batch AQ — Comparable Sales tab on the Deal Detail page.

   Layout:
     ┌─────────────────────────────────────────────────────────────┐
     │ Header: comp count · confidence chip · filter pills         │
     ├──────────────────────────────────┬──────────────────────────┤
     │ Map (Leaflet) — subject + comps  │ Stats card               │
     │                                  │  median price            │
     │                                  │  subject premium delta   │
     │                                  │  median $/sqft           │
     ├──────────────────────────────────┴──────────────────────────┤
     │ Sortable comp table                                         │
     └─────────────────────────────────────────────────────────────┘

   Data flow:
     - Mount → GET /api/deals/{id}/comps?radius=R&months=M
     - Filter change (radius / months) → refetch
     - Backend caches the response 24h, so repeated tab visits are free.

   Quota awareness:
     `source === "unavailable"` means RentCast didn't return — render
     an inline banner explaining the AVM provider is briefly out and
     the comp anchor on the AI Verdict will be skipped this session.

   Mobile:
     <md → map stacks above stats card → table scrolls horizontally.
*/

import { useCallback, useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Tooltip, CircleMarker } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ExternalLink, MapPin, Loader2, AlertTriangle, RefreshCw } from "lucide-react";
import {
  trackCompsTabViewed,
  trackCompsFilterChanged,
  trackCompClicked,
} from "@/lib/compsEvents";
import { pricePerSqft } from "@/lib/priceNormalisation";

// Leaflet default-icon shim — CRA's webpack can't resolve the bundled
// marker PNGs unless we patch the prototype. Use the public CDN.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const RADIUS_OPTIONS = [
  { value: "0.25", label: "0.25 mi" },
  { value: "0.5", label: "0.5 mi" },
  { value: "1", label: "1 mi" },
  { value: "2", label: "2 mi" },
];

const MONTHS_OPTIONS = [
  { value: "3", label: "Past 3 months" },
  { value: "6", label: "Past 6 months" },
  { value: "12", label: "Past 12 months" },
];

function money(n, opts = {}) {
  const v = Number(n || 0);
  return `$${v.toLocaleString("en-US", { maximumFractionDigits: 0, ...opts })}`;
}

function _confidenceTone(score) {
  if (score >= 80) return "bg-emerald-50 border-emerald-200 text-emerald-700";
  if (score >= 50) return "bg-amber-50 border-amber-200 text-amber-700";
  return "bg-rose-50 border-rose-200 text-rose-700";
}

function _premiumTone(pct) {
  if (pct === null || pct === undefined) return "text-slate-500";
  if (pct <= -3) return "text-emerald-700";
  if (pct < 5) return "text-amber-700";
  return "text-rose-700";
}

function _relativeDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const days = Math.floor((Date.now() - d.getTime()) / 86400000);
    if (days < 1) return "Today";
    if (days < 30) return `${days}d ago`;
    const months = Math.round(days / 30);
    if (months < 12) return `${months}mo ago`;
    return `${(months / 12).toFixed(1)}y ago`;
  } catch {
    return "—";
  }
}

export default function CompsTab({ dealId }) {
  const [radius, setRadius] = useState("0.5");
  const [months, setMonths] = useState("6");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/deals/${dealId}/comps`, {
        params: { radius_miles: Number(radius), months: Number(months) },
      });
      setData(res.data);
      trackCompsTabViewed({
        deal_id: dealId,
        comp_count: res.data?.stats?.comp_count ?? 0,
        confidence: res.data?.confidence?.score ?? 0,
      });
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load comparable sales");
    } finally {
      setLoading(false);
    }
  }, [dealId, radius, months]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRadiusChange = (v) => {
    setRadius(v);
    trackCompsFilterChanged({ deal_id: dealId, filter_type: "radius", new_value: v });
  };

  const handleMonthsChange = (v) => {
    setMonths(v);
    trackCompsFilterChanged({ deal_id: dealId, filter_type: "months", new_value: v });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- false positive; the
  // useMemo below already wraps this, but eslint's heuristic still flags it.
  const subject = data?.subject;
  const comps = useMemo(() => data?.comps || [], [data]);
  const stats = data?.stats;
  const confidence = data?.confidence;
  const unavailable = data?.source === "unavailable";

  // Map center — subject coords if present, else first comp, else US center.
  const mapCenter = useMemo(() => {
    if (subject?.latitude && subject?.longitude) return [subject.latitude, subject.longitude];
    const c = comps.find((c) => c.latitude && c.longitude);
    if (c) return [c.latitude, c.longitude];
    return [39.5, -98.35]; // continental US fallback
  }, [subject, comps]);

  const hasMapCoords = subject?.latitude && subject?.longitude;

  return (
    <div data-testid="comps-tab" className="space-y-5">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Comparable sales</h2>
          {data && (
            <p className="text-xs text-slate-500" data-testid="comps-summary">
              {stats?.comp_count ?? 0} comp{stats?.comp_count === 1 ? "" : "s"} ·{" "}
              {Number(radius)}mi · last {months} months
              {confidence && (
                <>
                  {" · "}
                  <span
                    data-testid="comps-confidence"
                    className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${_confidenceTone(
                      confidence.score,
                    )}`}
                  >
                    Confidence {confidence.score}/100
                  </span>
                </>
              )}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Select value={radius} onValueChange={handleRadiusChange}>
            <SelectTrigger className="h-8 w-[110px]" data-testid="comps-radius-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RADIUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={months} onValueChange={handleMonthsChange}>
            <SelectTrigger className="h-8 w-[140px]" data-testid="comps-months-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MONTHS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            variant="outline"
            onClick={load}
            disabled={loading}
            data-testid="comps-refresh-btn"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* Unavailable banner */}
      {unavailable && (
        <div
          data-testid="comps-unavailable-banner"
          className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            <p className="font-medium">Market data is briefly unavailable.</p>
            <p className="text-xs text-amber-700">
              We&rsquo;ll show comps as soon as our AVM provider responds. Existing
              AI verdicts continue to use whatever cached signals we already
              have for this deal.
            </p>
          </div>
        </div>
      )}

      {/* Load / error */}
      {loading && !data && (
        <div className="flex items-center justify-center rounded-xl border border-slate-200 bg-white p-10 text-sm text-slate-500">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Pulling comparable sales…
        </div>
      )}
      {error && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          <span>{error}</span>
          <Button size="sm" variant="outline" onClick={load}>
            Retry
          </Button>
        </div>
      )}

      {/* Map + Stats */}
      {data && !loading && (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_320px]">
          {/* Subject-vs-Comps PSF delta panel — Phase 47.3. Spans both
              columns at the top of the grid so the offer-anchor
              metric is impossible to miss. Tone driven by where the
              subject sits relative to the median PSF.
                <= median - 5%  → emerald (deal)
                median ±5%      → amber (fair)
                > median + 10%  → rose  (rich)
                middle band     → slate (neutral)
              */}
          {(() => {
            const medPsf = stats?.median_price_per_sqft;
            const subjPsf = pricePerSqft(stats?.subject_price, subject?.square_feet || subject?.square_footage);
            if (!medPsf || !subjPsf) return null;
            const deltaPct = ((subjPsf - medPsf) / medPsf) * 100;
            let tone = "slate";
            let label = "Within comp range";
            if (deltaPct <= -5) { tone = "emerald"; label = "Below comps median — leverage to negotiate"; }
            else if (deltaPct >= 10) { tone = "rose"; label = "Premium to comps — re-anchor the offer"; }
            else if (deltaPct >= 0) { tone = "amber"; label = "At or just above comps median"; }
            const toneClass = {
              emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
              amber:   "border-amber-200 bg-amber-50 text-amber-900",
              rose:    "border-rose-200 bg-rose-50 text-rose-900",
              slate:   "border-slate-200 bg-white text-slate-700",
            }[tone];
            return (
              <div
                data-testid="comps-psf-delta-card"
                className={`col-span-1 rounded-xl border px-4 py-3 shadow-sm lg:col-span-2 ${toneClass}`}
              >
                <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-baseline">
                  <p className="text-xs font-semibold uppercase tracking-wide">
                    Subject vs comps · $/sqft
                  </p>
                  <p className="text-xs">{label}</p>
                </div>
                <div className="mt-1 flex flex-wrap items-baseline gap-3 text-sm">
                  <span data-testid="comps-psf-subject" className="tabular-nums font-bold text-base">
                    ${Math.round(subjPsf).toLocaleString()}/sqft subject
                  </span>
                  <span className="text-xs opacity-70">vs</span>
                  <span data-testid="comps-psf-median" className="tabular-nums">
                    ${Math.round(medPsf).toLocaleString()}/sqft comps median
                  </span>
                  <span
                    data-testid="comps-psf-delta"
                    className="tabular-nums text-xs font-semibold"
                  >
                    ({deltaPct > 0 ? "+" : ""}{deltaPct.toFixed(1)}%)
                  </span>
                </div>
              </div>
            );
          })()}
          {/* Map */}
          <div
            data-testid="comps-map"
            className="overflow-hidden rounded-xl border border-slate-200 shadow-sm"
            style={{ height: "360px" }}
          >
            {hasMapCoords ? (
              <MapContainer
                center={mapCenter}
                zoom={14}
                scrollWheelZoom={false}
                style={{ height: "100%", width: "100%" }}
                attributionControl={false}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution="&copy; OpenStreetMap contributors"
                />
                {/* Subject = red circle */}
                <CircleMarker
                  center={[subject.latitude, subject.longitude]}
                  radius={10}
                  pathOptions={{
                    color: "#dc2626",
                    fillColor: "#ef4444",
                    fillOpacity: 0.9,
                    weight: 2,
                  }}
                >
                  <Tooltip permanent={false} direction="top">
                    <strong>Subject:</strong> {subject.address || "—"}
                  </Tooltip>
                </CircleMarker>
                {/* Comps */}
                {comps
                  .filter((c) => c.latitude && c.longitude)
                  .map((c) => (
                    <Marker
                      key={c.id || c.address}
                      position={[c.latitude, c.longitude]}
                      eventHandlers={{
                        click: () =>
                          trackCompClicked({
                            deal_id: dealId,
                            comp_id: c.id || c.address,
                            distance: c.distance_mi,
                          }),
                      }}
                    >
                      <Tooltip>
                        <div className="text-xs">
                          <strong>{c.address}</strong>
                          <br />
                          {money(c.effective_price)} · {c.bedrooms}bd/{c.bathrooms}ba
                          <br />
                          {Number(c.distance_mi || 0).toFixed(2)}mi away
                        </div>
                      </Tooltip>
                    </Marker>
                  ))}
              </MapContainer>
            ) : (
              <div className="flex h-full items-center justify-center bg-slate-50 text-xs text-slate-400">
                <MapPin className="mr-2 h-4 w-4" /> Map unavailable — missing
                subject coordinates.
              </div>
            )}
          </div>

          {/* Stats card */}
          <div
            data-testid="comps-stats-card"
            className="space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
          >
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Market snapshot
            </p>
            <dl className="space-y-3 text-sm">
              <div className="flex items-baseline justify-between gap-2">
                <dt className="text-slate-600">Median sale price</dt>
                <dd className="font-semibold text-slate-900 tabular-nums">
                  {stats?.median_price !== null && stats?.median_price !== undefined
                    ? money(stats.median_price)
                    : "—"}
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-2">
                <dt className="text-slate-600">Your offer</dt>
                <dd className="font-semibold text-slate-900 tabular-nums">
                  {stats?.subject_price !== null && stats?.subject_price !== undefined
                    ? money(stats.subject_price)
                    : "—"}
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-2 border-t border-slate-100 pt-3">
                {/* Batch AY — renamed from "Subject vs median" so the
                    label set is internally consistent ("Your offer"
                    + "Your offer vs median"). The number stays
                    relative to the EFFECTIVE price (your offer),
                    which is the only price that matters for ROI. */}
                <dt className="text-slate-600">Your offer vs median</dt>
                <dd
                  data-testid="comps-premium-pct"
                  className={`text-base font-semibold tabular-nums ${_premiumTone(
                    stats?.subject_premium_pct,
                  )}`}
                >
                  {stats?.subject_premium_pct !== null && stats?.subject_premium_pct !== undefined
                    ? `${stats.subject_premium_pct > 0 ? "+" : ""}${stats.subject_premium_pct}%`
                    : "—"}
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-2">
                <dt className="text-slate-600">Median $/sqft · $/sqm</dt>
                <dd className="font-semibold text-slate-900 tabular-nums">
                  {stats?.median_price_per_sqft
                    ? (
                      <>
                        ${stats.median_price_per_sqft.toFixed(0)}
                        <span className="ml-1 text-[10px] text-slate-400">
                          · ${Math.round(stats.median_price_per_sqft / 0.092903).toLocaleString()}/sqm
                        </span>
                      </>
                    )
                    : "—"}
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-2">
                <dt className="text-slate-600">Range</dt>
                <dd className="text-xs tabular-nums text-slate-700">
                  {stats?.min_price && stats?.max_price
                    ? `${money(stats.min_price)} – ${money(stats.max_price)}`
                    : "—"}
                </dd>
              </div>
            </dl>
            {confidence?.rationale && (
              <p
                data-testid="comps-confidence-rationale"
                className="border-t border-slate-100 pt-3 text-[11px] text-slate-500"
              >
                {confidence.rationale}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Comp table */}
      {data && !loading && comps.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <Table data-testid="comps-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Address</TableHead>
                  <TableHead className="text-right">Price</TableHead>
                  <TableHead className="text-right">Adjusted</TableHead>
                  <TableHead className="text-right">Distance</TableHead>
                  <TableHead className="text-right">Bd/Ba/Sqft</TableHead>
                  <TableHead className="text-right">$/sqft</TableHead>
                  <TableHead className="text-right">Listed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {comps.map((c) => {
                  const gmap = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(c.address || "")}`;
                  return (
                    <TableRow key={c.id || c.address} data-testid={`comp-row-${c.id || c.address}`}>
                      <TableCell className="max-w-[260px]">
                        <a
                          href={gmap}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 truncate text-slate-700 hover:text-indigo-700 hover:underline"
                          onClick={() =>
                            trackCompClicked({
                              deal_id: dealId,
                              comp_id: c.id || c.address,
                              distance: c.distance_mi,
                            })
                          }
                        >
                          <span className="truncate">{c.address}</span>
                          <ExternalLink className="h-3 w-3 flex-shrink-0 text-slate-400" />
                        </a>
                        {c.is_sold && (
                          <span className="ml-1 inline-flex rounded-full bg-emerald-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-emerald-700">
                            Sold
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {money(c.effective_price)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-slate-700">
                        {c.adjusted_price ? money(c.adjusted_price) : "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-slate-600">
                        {Number(c.distance_mi || 0).toFixed(2)}mi
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-slate-600">
                        {c.bedrooms}/{c.bathrooms}/{c.square_footage}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-slate-600">
                        {c.price_per_sqft ? (
                          <div>
                            <div>${c.price_per_sqft.toFixed(0)}</div>
                            {/* Phase 47.3 — PSM derived row beneath the
                                existing PSF cell (1 sqft = 0.092903 sqm). */}
                            <div className="text-[10px] text-slate-400">
                              ${Math.round(c.price_per_sqft / 0.092903).toLocaleString()}/sqm
                            </div>
                          </div>
                        ) : "—"}
                      </TableCell>
                      <TableCell className="text-right text-xs text-slate-500">
                        <span title={c.effective_date}>{_relativeDate(c.effective_date)}</span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Empty state */}
      {data && !loading && comps.length === 0 && !unavailable && (
        <div
          data-testid="comps-empty"
          className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center"
        >
          <MapPin className="mx-auto h-8 w-8 text-slate-400" />
          <p className="mt-3 text-sm font-medium text-slate-700">
            No comparable sales in your radius and time window.
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Try expanding to 1mi or extending the time window to 12 months.
          </p>
        </div>
      )}
    </div>
  );
}
